import { useObserver } from 'mobx-react-lite';

import type { Dispensaries } from 'src/gql/graphql';
import CartItem from 'src/state/cart-item';
import useUser from 'hooks/use-user';
import useUI from 'hooks/use-ui';
import useCart from 'hooks/use-cart';
import useDispensary from 'src/dispensary/hooks/use-dispensary';

import type {
  Cart as RemoteCart,
  CartItem as RemoteCartItem,
  User as RemoteUser,
  CommerceComponentsDataInterface,
} from '@dutchiesdk/ecommerce-extensions-sdk';

import CartStore from 'src/state/cart';
import UserStore from 'src/state/user';

import { useBridgeActions } from './bridge-actions';
import { useBridgeDataLoaders } from './bridge-data-loaders';
import { mapDispensaryToRemote } from './bridge-map-dispensary';

export const useBridgeDataInterface = (): CommerceComponentsDataInterface => {
  const { dispensary } = useDispensary() as { dispensary?: Dispensaries | undefined };
  const User = useUser();
  const UI = useUI();
  const Cart = useCart();
  const actions = useBridgeActions();
  const dataLoaders = useBridgeDataLoaders();

  const cart = useObserver(() => (Cart ? mapCartToRemote(Cart) : undefined));
  // `user` should only be populated for authenticated sessions -- `User` (the MobX store) is a
  // singleton that's always truthy, even for guests, so gate on `isLoggedIn` rather than presence.
  const user = useObserver(() => (User?.isLoggedIn ? mapUserToRemote(User) : undefined));

  return {
    actions,
    cart,
    dataLoaders,
    location: mapDispensaryToRemote(dispensary),
    menuContext: UI.isKiosk ? 'kiosk' : 'store-front',
    user,
  };
};

const mapCartItem = (item: CartItem): RemoteCartItem => ({
  name: item.product?.name,
  price: item.price,
  // @ts-expect-error product.id is Maybe<string>; resolve when tightening SDK types.
  productId: item.product?.id,
  quantity: item.quantity,
});

const mapCartToRemote = (cart: CartStore): RemoteCart => ({
  // @ts-expect-error costBreakdown may be undefined vs RemoteCart; decide defaults before tightening types.
  discount: cart.costBreakdown.credit,
  items: Object.values(cart.items).map(mapCartItem),
  // @ts-expect-error costBreakdown may be undefined vs RemoteCart; decide defaults before tightening types.
  subtotal: cart.costBreakdown.subtotal,
  // @ts-expect-error costBreakdown may be undefined vs RemoteCart; decide defaults before tightening types.
  tax: cart.costBreakdown.tax,
  // @ts-expect-error costBreakdown may be undefined vs RemoteCart; decide defaults before tightening types.
  total: cart.costBreakdown.total,
});

const mapUserToRemote = (user: UserStore): RemoteUser => ({
  birthday: user.birthday,
  email: user.email ?? '',
  firstName: user.firstName,
  lastName: user.lastName,
});
